import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextareaAutosize,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { Table } from '@tanstack/react-table';

type MultiLineEditorCellProps = {
  value: unknown;
  rowIndex: number;
  columnId: string;
  table: Table<Record<string, unknown>>;
};

export const MultiLineEditorCell = ({
  value,
  rowIndex,
  columnId,
  table,
}: MultiLineEditorCellProps) => {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(String(value ?? ''));

  const openEditor = () => {
    setDraft(String(value ?? ''));
    setOpen(true);
  };

  const closeEditor = () => setOpen(false);

  const save = () => {
    table.options.meta?.updateData(rowIndex, columnId, draft);
    closeEditor();
  };

  const handleTab = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Tab') return;

    event.preventDefault();
    const textarea = event.currentTarget;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    // Use DOM value as source of truth to avoid stale draft under React state batching
    const current = textarea.value;
    setDraft(`${current.slice(0, start)}\t${current.slice(end)}`);
    requestAnimationFrame(() => {
      textarea.selectionStart = start + 1;
      textarea.selectionEnd = start + 1;
    });
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={openEditor}
        title="Edit text"
        aria-label={`Edit text: ${String(value ?? '')}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          width: '100%',
          height: '100%',
          minWidth: 0,
          border: 0,
          padding: 0,
          background: 'transparent',
          color: 'inherit',
          textAlign: 'left',
          font: 'inherit',
          cursor: 'pointer',
        }}
      >
        <Box
          sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {String(value ?? '')}
        </Box>
      </Box>

      <Dialog
        open={open}
        onClose={closeEditor}
        PaperProps={{ sx: { width: 600, maxWidth: 'calc(100% - 32px)', minHeight: 400 } }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          Edit Text
          <IconButton
            aria-label="Close"
            onClick={closeEditor}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextareaAutosize
            aria-label="Edit text"
            autoFocus
            minRows={12}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleTab}
            style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box', padding: 8 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor}>Cancel</Button>
          <Button variant="contained" onClick={save}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
